import {
  Color3,
  Mesh,
  MeshBuilder,
  PhysicsAggregate,
  PhysicsShapeType,
  Vector3,
} from '@babylonjs/core';
import { createArenaScene, createRobotMesh, mat, signedAngleTo } from './arena';
import type { ArenaScene } from './arena';
import { createHavokPlugin } from './havok';
import type { RobotValues, RulePack, SimCallbacks, SimEvent } from '../types';

type Mode = 'sumo' | 'battle';

interface BotCfg {
  weight: number;
  torque: number;
  grip: number;
  maxSpeed: number;
  sensorRange: number;
  sensorCone: number; // radians
  reactionMs: number;
  strategy: string;
}

interface Bot {
  mesh: Mesh;
  agg: PhysicsAggregate;
  cfg: BotCfg;
  name: string;
  state: 'search' | 'attack' | 'evade';
  evadeUntil: number;
  edgeSince: number | null;
  energy: number;
  lastHitAt: number;
  zoneSince: number | null;
  lastContactAt: number;
  slipLogged: number;
}

const WHEEL_RADIUS = 0.04;

function num(v: number | string | boolean | undefined, fallback: number): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function botCfg(values: RobotValues, fallbackStrategy: string): BotCfg {
  return {
    weight: num(values.weightKg, 1.8),
    torque: num(values.torqueNm, 0.5),
    grip: num(values.gripMu, 0.8),
    maxSpeed: num(values.maxSpeed, 0.7),
    sensorRange: num(values.sensorRange, 0.8),
    sensorCone: (num(values.sensorCone, 40) * Math.PI) / 180,
    reactionMs: num(values.reactionMs, 120),
    strategy: typeof values.strategy === 'string' ? values.strategy : fallbackStrategy,
  };
}

/**
 * Havok-powered push arena: Sumo (circular ring, fall out = lose) and
 * Battle (walled square, energy + zone control).
 */
export class PushSim {
  private arena: ArenaScene | null = null;
  private bots: Bot[] = [];
  private disposed = false;
  private events: SimEvent[] = [];
  private speedSeries: number[] = [];
  private time = 0;
  private started = false;
  private finished = false;
  private score = 0;
  private hudAccum = 0;
  private speedAccum = 0;
  private ringRadius = 0.77;
  private halfArena = 1;
  private ringTopY = 0.06;

  private canvas: HTMLCanvasElement;
  private mode: Mode;
  private pack: RulePack;
  private robot: RobotValues;
  private cb: SimCallbacks;

  constructor(canvas: HTMLCanvasElement, mode: Mode, pack: RulePack, robot: RobotValues, cb: SimCallbacks) {
    this.canvas = canvas;
    this.mode = mode;
    this.pack = pack;
    this.robot = robot;
    this.cb = cb;
  }

  async start(): Promise<void> {
    const arena = createArenaScene(this.canvas, this.mode === 'sumo' ? 2.4 : 3.2);
    this.arena = arena;
    const { scene } = arena;

    const plugin = await createHavokPlugin();
    if (this.disposed) return;
    scene.enablePhysics(new Vector3(0, -9.81, 0), plugin);

    const arenaFriction = num(this.pack.arena?.friction, 0.85);

    /* ---------- environment ---------- */
    const floor = MeshBuilder.CreateBox('floor', { width: 8, depth: 8, height: 0.1 }, scene);
    floor.position.y = -0.25;
    floor.material = mat(scene, 'floor-mat', new Color3(0.05, 0.07, 0.12));
    new PhysicsAggregate(floor, PhysicsShapeType.BOX, { mass: 0, friction: 0.6 }, scene);

    let startA: Vector3;
    let startB: Vector3;

    if (this.mode === 'sumo') {
      const diameter = num(this.pack.arena?.diameterCm, 154) / 100;
      this.ringRadius = diameter / 2;
      const border = 0.05;

      const ring = MeshBuilder.CreateCylinder('ring', { diameter, height: 0.06, tessellation: 64 }, scene);
      ring.position.y = 0.03;
      ring.material = mat(scene, 'ring-white', new Color3(0.92, 0.92, 0.95), 0.15);
      new PhysicsAggregate(ring, PhysicsShapeType.CYLINDER, { mass: 0, friction: arenaFriction }, scene);

      const surface = MeshBuilder.CreateCylinder(
        'surface',
        { diameter: diameter - border * 2, height: 0.062, tessellation: 64 },
        scene,
      );
      surface.position.y = 0.031;
      surface.material = mat(scene, 'ring-black', new Color3(0.06, 0.06, 0.08));

      const d = this.ringRadius * 0.5;
      startA = new Vector3(0, this.ringTopY + 0.06, -d);
      startB = new Vector3(0, this.ringTopY + 0.06, d);
    } else {
      const width = num(this.pack.arena?.widthCm, 200) / 100;
      this.halfArena = width / 2;
      this.ringTopY = 0.0;

      const ground = MeshBuilder.CreateBox('bground', { width, depth: width, height: 0.05 }, scene);
      ground.position.y = -0.025;
      ground.material = mat(scene, 'bground-mat', new Color3(0.09, 0.09, 0.13));
      new PhysicsAggregate(ground, PhysicsShapeType.BOX, { mass: 0, friction: arenaFriction }, scene);

      const wallMat = mat(scene, 'wall-mat', new Color3(0.25, 0.2, 0.45), 0.2);
      const wallH = 0.12;
      const walls: [number, number, number, number][] = [
        [0, -this.halfArena - 0.025, width + 0.1, 0.05],
        [0, this.halfArena + 0.025, width + 0.1, 0.05],
        [-this.halfArena - 0.025, 0, 0.05, width + 0.1],
        [this.halfArena + 0.025, 0, 0.05, width + 0.1],
      ];
      walls.forEach(([x, z, w, d], i) => {
        const wall = MeshBuilder.CreateBox(`wall${i}`, { width: w, depth: d, height: wallH }, scene);
        wall.position.set(x, wallH / 2, z);
        wall.material = wallMat;
        new PhysicsAggregate(wall, PhysicsShapeType.BOX, { mass: 0, friction: 0.4 }, scene);
      });

      const zone = MeshBuilder.CreateCylinder('zone', { diameter: 0.6, height: 0.005, tessellation: 48 }, scene);
      zone.position.y = 0.003;
      zone.material = mat(scene, 'zone-mat', new Color3(0.55, 0.35, 1), 0.6);

      const d = this.halfArena * 0.6;
      startA = new Vector3(-d, 0.06, -d);
      startB = new Vector3(d, 0.06, d);
    }

    /* ---------- robots ---------- */
    const userCfg = botCfg(this.robot, 'Balanced');
    const oppValues: RobotValues =
      this.mode === 'sumo'
        ? { weightKg: 1.9, torqueNm: 0.55, gripMu: 0.85, maxSpeed: 0.65, sensorRange: 0.8, sensorCone: 45, reactionMs: 140, strategy: 'Hunter (attack on sight)' }
        : { weightKg: 2.0, torqueNm: 0.6, gripMu: 0.85, maxSpeed: 0.75, sensorRange: 1.1, sensorCone: 60, reactionMs: 140, strategy: 'Balanced' };

    this.bots = [
      this.makeBot('You', new Color3(0.1, 0.6, 1), userCfg, startA, 0),
      this.makeBot('Opponent', new Color3(1, 0.35, 0.2), botCfg(oppValues, 'Balanced'), startB, Math.PI),
    ];

    /* ---------- countdown then run ---------- */
    const startDelay = num(this.pack.match?.startDelaySec, 3);
    const duration = num(
      this.mode === 'sumo' ? this.pack.match?.roundDurationSec : this.pack.match?.durationSec,
      this.mode === 'sumo' ? 90 : 120,
    );
    const startEnergy = num(this.pack.match?.startEnergy, 100);
    this.bots.forEach((b) => (b.energy = startEnergy));

    let countdown = startDelay;
    this.event('info', `Countdown: robots start in ${startDelay}s`);

    arena.scene.onBeforeRenderObservable.add(() => {
      if (this.finished || this.disposed) return;
      const dt = arena.engine.getDeltaTime() / 1000;
      if (dt <= 0) return;

      if (!this.started) {
        countdown -= dt;
        this.pushHud(`Starting in ${Math.max(0, countdown).toFixed(1)}s`);
        if (countdown <= 0) {
          this.started = true;
          this.event('info', 'GO!');
        }
        return;
      }

      this.time += dt;
      this.bots.forEach((bot, i) => this.drive(bot, this.bots[1 - i], dt));
      this.judge(duration, dt);
      this.sample(dt);
    });
  }

  private makeBot(name: string, color: Color3, cfg: BotCfg, pos: Vector3, rotY: number): Bot {
    const scene = this.arena!.scene;
    const mesh = createRobotMesh(scene, name, { color });
    mesh.position.copyFrom(pos);
    mesh.rotation = new Vector3(0, rotY, 0);
    const agg = new PhysicsAggregate(mesh, PhysicsShapeType.BOX, { mass: cfg.weight, friction: cfg.grip, restitution: 0.15 }, scene);
    // Lower the center of mass so robots resist tipping (like a real low battery mount).
    agg.body.setMassProperties({ centerOfMass: new Vector3(0, -0.03, 0) });
    return {
      mesh,
      agg,
      cfg,
      name,
      state: 'search',
      evadeUntil: 0,
      edgeSince: null,
      energy: 100,
      lastHitAt: -10,
      zoneSince: null,
      lastContactAt: -10,
      slipLogged: -10,
    };
  }

  /* ---------------- robot controller (strategy + physics) ---------------- */

  private drive(bot: Bot, other: Bot, dt: number): void {
    void dt;
    const body = bot.agg.body;
    const pos = bot.mesh.position;
    const fwd = bot.mesh.forward;

    /* --- sensors --- */
    const toOther = other.mesh.position.subtract(pos);
    const dist = Math.hypot(toOther.x, toOther.z);
    const angle = signedAngleTo(fwd, toOther);
    const sees = dist < bot.cfg.sensorRange && Math.abs(angle) < bot.cfg.sensorCone / 2;

    const r = Math.hypot(pos.x, pos.z);
    const nearEdge =
      this.mode === 'sumo'
        ? r > this.ringRadius - 0.09
        : Math.max(Math.abs(pos.x), Math.abs(pos.z)) > this.halfArena - 0.14;

    /* --- edge reaction with configurable delay --- */
    const nowMs = this.time * 1000;
    if (nearEdge) {
      if (bot.edgeSince === null) bot.edgeSince = nowMs;
      if (nowMs - bot.edgeSince > bot.cfg.reactionMs && bot.state !== 'evade') {
        bot.state = 'evade';
        bot.evadeUntil = nowMs + 650;
        if (bot.name === 'You') this.event('warn', `Edge detected at ${this.time.toFixed(1)}s — reversing`);
      }
    } else {
      bot.edgeSince = null;
    }
    if (bot.state === 'evade' && nowMs > bot.evadeUntil) bot.state = 'search';
    if (bot.state !== 'evade') bot.state = sees ? 'attack' : 'search';

    /* --- desired motion from strategy --- */
    let throttle = 0; // -1..1 along forward
    let turn = 0; // rad/s

    const aggressive = bot.cfg.strategy.startsWith('Hunter') || bot.cfg.strategy === 'Aggressive';
    const defensive = bot.cfg.strategy.startsWith('Defender') || bot.cfg.strategy.startsWith('Defensive');

    if (bot.state === 'evade') {
      throttle = -1;
      turn = 2.5;
    } else if (bot.state === 'attack') {
      turn = Math.max(-3, Math.min(3, angle * 4));
      throttle = Math.abs(angle) < 0.5 ? 1 : 0.35;
    } else {
      // search
      if (defensive) {
        const toCenter = new Vector3(-pos.x, 0, -pos.z);
        const centerDist = Math.hypot(toCenter.x, toCenter.z);
        if (centerDist > 0.12) {
          turn = Math.max(-2.5, Math.min(2.5, signedAngleTo(fwd, toCenter) * 3));
          throttle = 0.5;
        } else {
          turn = 2.2; // scan in place at center
        }
      } else {
        turn = aggressive ? 2.0 : 2.8;
        throttle = aggressive ? 0.45 : 0.2;
      }
    }

    /* --- physics: torque → force, capped by traction --- */
    const driveForce = (bot.cfg.torque * 2) / WHEEL_RADIUS;
    const traction = bot.cfg.grip * bot.cfg.weight * 9.81;
    const force = Math.min(Math.abs(throttle) * driveForce, traction) * Math.sign(throttle);
    if (Math.abs(throttle) * driveForce > traction * 1.05 && this.time - bot.slipLogged > 2) {
      bot.slipLogged = this.time;
      if (bot.name === 'You') this.event('warn', 'Wheels slipping — torque exceeds traction');
    }

    const flat = new Vector3(fwd.x, 0, fwd.z).normalize();
    body.applyForce(flat.scale(force), pos);

    // speed cap (motor max RPM / battery limit)
    const v = body.getLinearVelocity();
    const hSpeed = Math.hypot(v.x, v.z);
    if (hSpeed > bot.cfg.maxSpeed) {
      const k = bot.cfg.maxSpeed / hSpeed;
      body.setLinearVelocity(new Vector3(v.x * k, v.y, v.z * k));
    }

    // steering
    const av = body.getAngularVelocity();
    body.setAngularVelocity(new Vector3(av.x * 0.5, turn, av.z * 0.5));

    /* --- battle extras: contact hits + zone tracking --- */
    if (this.mode === 'battle') {
      if (dist < 0.27) {
        bot.lastContactAt = this.time;
        const ov = other.agg.body.getLinearVelocity();
        const relSpeed = Math.hypot(v.x - ov.x, v.z - ov.z);
        if (relSpeed > 0.55 && this.time - other.lastHitAt > 1) {
          other.lastHitAt = this.time;
          const myMomentum = bot.cfg.weight * hSpeed;
          const dmg = Math.round(6 + myMomentum * 4);
          other.energy = Math.max(0, other.energy - dmg);
          this.event(
            bot.name === 'You' ? 'good' : 'bad',
            `${bot.name} lands a hit! ${other.name} energy -${dmg} (momentum ${myMomentum.toFixed(1)} kg·m/s)`,
          );
          if (bot.name === 'You') this.score += num(this.pack.scoring?.hardHit, 3);
        }
      }
      const inZone = Math.hypot(pos.x, pos.z) < 0.3;
      if (inZone) {
        if (bot.zoneSince === null) bot.zoneSince = this.time;
        if (this.time - bot.zoneSince >= 5) {
          bot.zoneSince = this.time;
          if (bot.name === 'You') {
            this.score += num(this.pack.scoring?.zoneControlPer5Sec, 2);
            this.event('good', 'Center zone controlled +' + num(this.pack.scoring?.zoneControlPer5Sec, 2));
          }
        }
      } else {
        bot.zoneSince = null;
      }
    } else if (dist < 0.27) {
      bot.lastContactAt = this.time;
    }
  }

  /* ---------------- judging (rule engine) ---------------- */

  private judge(duration: number, dt: number): void {
    void dt;
    const [user, opp] = this.bots;
    const sc = this.pack.scoring ?? {};

    if (this.mode === 'sumo') {
      const out = (b: Bot) => b.mesh.position.y < this.ringTopY - 0.08 || Math.hypot(b.mesh.position.x, b.mesh.position.z) > this.ringRadius + 0.12;
      const userOut = out(user);
      const oppOut = out(opp);

      if (userOut && oppOut) {
        this.finish(false, 'Both robots left — round repeated (draw)', 0);
      } else if (oppOut) {
        const pushed = this.time - opp.lastContactAt < 1.5;
        const pts = num(pushed ? sc.pushOpponentOut : sc.opponentLeaves, pushed ? 3 : 2);
        this.finish(true, pushed ? 'You pushed the opponent out of the ring!' : 'Opponent left the ring on its own', pts);
      } else if (userOut) {
        this.finish(false, 'Your robot left the ring', num(sc.selfOut, -2));
      } else if (this.time >= duration) {
        this.finish(false, 'Time up — round drawn on points', num(sc.surviveToEnd, 1));
      }
    } else {
      if (opp.energy <= 0) {
        this.finish(true, 'Opponent energy depleted — knockout!', num(sc.energyKO, 10));
      } else if (user.energy <= 0) {
        this.finish(false, 'Your energy reached zero — knocked out', 0);
      } else if (this.time >= duration) {
        const bonus = num(sc.surviveToEnd, 1);
        this.score += bonus;
        const won = this.score > 0 && user.energy >= opp.energy;
        this.finish(won, won ? 'Time up — you win on points and energy' : 'Time up — opponent wins on energy', 0);
      }
    }
  }

  private finish(won: boolean, outcome: string, pts: number): void {
    if (this.finished) return;
    this.finished = true;
    this.score += pts;
    const [user, opp] = this.bots;

    const metrics: Record<string, string> = {
      'Run time': `${this.time.toFixed(1)} s`,
      'Max speed': `${Math.max(0, ...this.speedSeries).toFixed(2)} m/s`,
      'Pushing force (traction-limited)': `${Math.min((user.cfg.torque * 2) / WHEEL_RADIUS, user.cfg.grip * user.cfg.weight * 9.81).toFixed(1)} N`,
      'Edge reaction delay': `${user.cfg.reactionMs} ms`,
    };
    if (this.mode === 'battle') {
      metrics['Your energy'] = `${Math.round(user.energy)} / 100`;
      metrics['Opponent energy'] = `${Math.round(opp.energy)} / 100`;
    }

    this.event(won ? 'good' : 'bad', outcome);
    this.cb.onEnd({
      competition: this.mode === 'sumo' ? 'sumo' : 'battle',
      outcome,
      won,
      score: this.score,
      timeSec: this.time,
      metrics,
      events: this.events,
      speedSeries: this.speedSeries,
    });
  }

  /* ---------------- HUD + sampling ---------------- */

  private sample(dt: number): void {
    this.speedAccum += dt;
    if (this.speedAccum >= 0.1) {
      this.speedAccum = 0;
      const v = this.bots[0].agg.body.getLinearVelocity();
      this.speedSeries.push(Math.hypot(v.x, v.z));
    }
    this.hudAccum += dt;
    if (this.hudAccum >= 0.12) {
      this.hudAccum = 0;
      this.pushHud(this.bots[0].state.toUpperCase());
    }
  }

  private pushHud(status: string): void {
    const [user, opp] = this.bots;
    const pos = user.mesh.position;
    const toOther = opp.mesh.position.subtract(pos);
    const dist = Math.hypot(toOther.x, toOther.z);
    const sees = dist < user.cfg.sensorRange && Math.abs(signedAngleTo(user.mesh.forward, toOther)) < user.cfg.sensorCone / 2;
    const v = user.agg.body.getLinearVelocity();

    const sensors: Record<string, string> = {
      'Opponent detected': sees ? `yes (${(dist * 100).toFixed(0)} cm)` : 'no',
      Speed: `${Math.hypot(v.x, v.z).toFixed(2)} m/s`,
    };
    if (this.mode === 'sumo') {
      const r = Math.hypot(pos.x, pos.z);
      sensors['Edge sensor'] = r > this.ringRadius - 0.09 ? 'WHITE BORDER!' : 'black surface';
      sensors['Distance to edge'] = `${Math.max(0, (this.ringRadius - r) * 100).toFixed(0)} cm`;
    } else {
      sensors['Your energy'] = `${Math.round(user.energy)}`;
      sensors['Opponent energy'] = `${Math.round(opp.energy)}`;
      sensors['Center zone'] = Math.hypot(pos.x, pos.z) < 0.3 ? 'CONTROLLED' : 'free';
    }

    this.cb.onTick({ timeSec: this.time, status, sensors });
  }

  private event(type: SimEvent['type'], msg: string): void {
    this.events.push({ t: this.time, type, msg });
  }

  dispose(): void {
    this.disposed = true;
    this.arena?.dispose();
    this.arena = null;
  }
}
